import {
  Box,
  Flex,
  Icon,
  Text,
  useToast,
  VStack,
  HStack,
  Divider,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useMemo, useState, useRef } from "react";
import {
  MdLocationOn,
  MdEvStation,
  MdCloudUpload,
  MdCheckCircle,
  MdError,
  MdCode,
  MdContentCopy,
} from "react-icons/md";
import useAxios from "axios-hooks";
import OcpiLocationsTable from "./components/OcpiLocationsTable";
import OcpiTariffsTable from "./components/OcpiTariffsTable";
import OcpiStationsTable from "./components/OcpiStationsTable";
import styled from "@emotion/styled";
import Card from "components/card/Card";

const GlassMorphismCard = styled(Card)`
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(245, 125, 54, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    border-color: rgba(245, 125, 54, 0.4);
    box-shadow: 0 8px 32px rgba(245, 125, 54, 0.15);
  }
`;

const RoamingScreen = () => {
  const toast = useToast();
  const copyTextareaRef = useRef(null);
  const [broadcastResults, setBroadcastResults] = useState(null);
  const [rowBroadcasting, setRowBroadcasting] = useState(new Set());
  const [viewModal, setViewModal] = useState({
    open: false,
    title: "",
    body: null,
    loading: false,
  });

  const textColor = useColorModeValue("gray.700", "white");

  // ── List fetches ──────────────────────────────────────────────────────────
  const [{ data: locationsData, loading: locListLoading }, refetchLocations] =
    useAxios({ method: "GET", url: "/api/locations" });

  const [{ data: stationsData, loading: stListLoading }, refetchStations] =
    useAxios({ method: "GET", url: "/api/stations" });

  const locations = useMemo(() => locationsData?.data || [], [locationsData]);
  const stations = useMemo(() => stationsData?.data || [], [stationsData]);

  // Stations tab only manages OCPI enablement for stations whose location is
  // already OCPI-enabled — enabling a station is meaningless otherwise.
  const ocpiEnabledLocationIds = useMemo(
    () =>
      new Set(
        locations.filter((loc) => loc.IsOcpiEnabled).map((loc) => loc.Id),
      ),
    [locations],
  );
  const ocpiEligibleStations = useMemo(
    () => stations.filter((station) => ocpiEnabledLocationIds.has(station.LocationId)),
    [stations, ocpiEnabledLocationIds],
  );

  // ── Broadcast all ─────────────────────────────────────────────────────────
  const [{ loading: allLocLoading }, executeBroadcastAllLocations] = useAxios(
    { method: "POST", url: "/api/roaming/locations/broadcast" },
    { manual: true },
  );
  const [{ loading: allTarLoading }, executeBroadcastAllTariffs] = useAxios(
    { method: "POST", url: "/api/roaming/tariffs/broadcast" },
    { manual: true },
  );

  // ── Per-row broadcast (lazy) ──────────────────────────────────────────────
  const [, execSingleBroadcast] = useAxios({}, { manual: true });

  // ── View body (lazy) ──────────────────────────────────────────────────────
  const [, execViewBody] = useAxios({}, { manual: true });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showBroadcastResult = (type, result) => {
    setBroadcastResults({ type, ...result });
    toast({
      title: `${type === "locations" ? "Locations" : "Tariffs"} Broadcast Complete`,
      description: `Broadcasted ${result.broadcasted || 0} of ${result.total || 0}`,
      status: (result.errors?.length || 0) > 0 ? "warning" : "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const handleBroadcastAll = async (type) => {
    setBroadcastResults(null);
    try {
      const fn =
        type === "locations"
          ? executeBroadcastAllLocations
          : executeBroadcastAllTariffs;
      const resp = await fn();
      showBroadcastResult(type, resp?.data?.data || {});
    } catch (err) {
      toast({
        title: `Error Broadcasting ${type === "locations" ? "Locations" : "Tariffs"}`,
        description:
          err?.response?.data?.error || err?.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBroadcastRow = async (type, id) => {
    const key = `${type}-${id}`;
    setRowBroadcasting((prev) => new Set(prev).add(key));
    try {
      const url =
        type === "location"
          ? `/api/roaming/locations/${id}/broadcast`
          : `/api/roaming/tariffs/${id}/broadcast`;
      await execSingleBroadcast({ method: "POST", url });
      toast({
        title: "Broadcast Successful",
        description: `${type === "location" ? "Location" : "Tariff"} sent to OCPI peers`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Broadcast Failed",
        description:
          err?.response?.data?.error || err?.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRowBroadcasting((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleViewBody = async (type, id, title) => {
    setViewModal({ open: true, title, body: null, loading: true });
    try {
      const url =
        type === "location"
          ? `/api/roaming/locations/${id}/body`
          : `/api/roaming/tariffs/${id}/body`;
      const resp = await execViewBody({ method: "GET", url });
      setViewModal({
        open: true,
        title,
        body: resp?.data?.data ?? null,
        loading: false,
      });
    } catch (err) {
      setViewModal({ open: false, title: "", body: null, loading: false });
      toast({
        title: "Failed to load OCPI body",
        description:
          err?.response?.data?.error || err?.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCopyJson = async () => {
    if (!viewModal.body) return;
    const text = JSON.stringify(viewModal.body, null, 2);

    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        toast({
          title: "Copied to clipboard",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        return;
      } catch {
        // fall through to legacy method
      }
    }

    // Fallback: hidden textarea + execCommand
    const ta = copyTextareaRef.current;
    if (!ta) return;
    ta.value = text;
    ta.style.display = "block";
    ta.select();
    ta.setSelectionRange(0, 99999);
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    ta.style.display = "none";
    window.getSelection().removeAllRanges();

    if (ok) {
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Copy failed",
        description: "Clipboard access denied. Please select and copy manually.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const closeModal = () =>
    setViewModal({ open: false, title: "", body: null, loading: false });

  // ── Shared tab button style ───────────────────────────────────────────────
  const tabSx = {
    borderRadius: "lg",
    px: { base: 3, md: 4 },
    py: 2,
    fontWeight: "500",
    color: "gray.600",
    position: "relative",
    zIndex: 1,
    minW: "fit-content",
    whiteSpace: "nowrap",
    _selected: {
      bg: "rgba(245, 125, 54, 0.15)",
      color: "#f57d36",
      fontWeight: "600",
      boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
    },
    _hover: { bg: "rgba(245, 125, 54, 0.08)", color: "#f57d36" },
    transition: "all 0.15s",
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Flex align="center" mb={6} gap={3}>
        <Icon as={MdCloudUpload} boxSize={7} color="#f57d36" />
        <Box>
          <Text fontSize="2xl" fontWeight="700" color={textColor}>
            OCPI Roaming
          </Text>
          <Text fontSize="sm" color="gray.500">
            Broadcast locations and tariffs to OCPI roaming partners
          </Text>
        </Box>
      </Flex>

      {/* Tabs */}
      <Tabs variant="unstyled" isLazy>
        <TabList
          gap={{ base: 2, md: 4 }}
          px={{ base: 2, md: 1 }}
          py={2}
          bg="rgba(255,255,255,0.6)"
          backdropFilter="blur(12px)"
          borderRadius="xl"
          border="1px solid rgba(245,125,54,0.15)"
          position="relative"
          overflow="hidden"
          mb={5}
        >
          <Tab {...tabSx}>
            <Flex align="center" gap={{ base: 1, md: 2 }}>
              <Icon as={MdLocationOn} boxSize={{ base: 3.5, md: 4 }} />
              <Text fontSize={{ base: "sm", md: "md" }}>Locations</Text>
            </Flex>
          </Tab>
          <Tab {...tabSx}>
            <Flex align="center" gap={{ base: 1, md: 2 }}>
              <Icon as={MdEvStation} boxSize={{ base: 3.5, md: 4 }} />
              <Text fontSize={{ base: "sm", md: "md" }}>Tariffs</Text>
            </Flex>
          </Tab>
          <Tab {...tabSx}>
            <Flex align="center" gap={{ base: 1, md: 2 }}>
              <Icon as={MdEvStation} boxSize={{ base: 3.5, md: 4 }} />
              <Text fontSize={{ base: "sm", md: "md" }}>Stations</Text>
            </Flex>
          </Tab>
        </TabList>

        <TabPanels>
          {/* ── Locations Tab ─────────────────────────────────────── */}
          <TabPanel p={0}>
            <OcpiLocationsTable
              locations={locations}
              refetchLocations={refetchLocations}
              locListLoading={locListLoading}
              handleBroadcastAll={handleBroadcastAll}
              allLocLoading={allLocLoading}
              handleBroadcastRow={handleBroadcastRow}
              handleViewBody={handleViewBody}
              rowBroadcasting={rowBroadcasting}
            />
          </TabPanel>

          {/* ── Tariffs Tab ───────────────────────────────────────── */}
          <TabPanel p={0}>
            <OcpiTariffsTable
              stations={stations}
              refetchStations={refetchStations}
              stListLoading={stListLoading}
              handleBroadcastAll={handleBroadcastAll}
              allTarLoading={allTarLoading}
              handleBroadcastRow={handleBroadcastRow}
              handleViewBody={handleViewBody}
              rowBroadcasting={rowBroadcasting}
            />
          </TabPanel>

          {/* ── Stations Tab ──────────────────────────────────────── */}
          <TabPanel p={0}>
            <OcpiStationsTable
              stations={ocpiEligibleStations}
              refetchStations={() => {
                refetchLocations();
                refetchStations();
              }}
              stListLoading={stListLoading || locListLoading}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Broadcast Results */}
      {broadcastResults && (
        <GlassMorphismCard mt={5}>
          <Box p={6}>
            <Flex align="center" mb={4}>
              <Icon
                as={
                  (broadcastResults.errors?.length || 0) > 0
                    ? MdError
                    : MdCheckCircle
                }
                boxSize={6}
                color={
                  (broadcastResults.errors?.length || 0) > 0
                    ? "orange.400"
                    : "green.500"
                }
                mr={3}
              />
              <Text fontSize="lg" fontWeight="600">
                {broadcastResults.type === "locations" ? "Location" : "Tariff"}{" "}
                Broadcast Results
              </Text>
            </Flex>
            <Divider mb={4} borderColor="rgba(245, 125, 54, 0.15)" />
            <HStack spacing={8} mb={4}>
              {[
                {
                  label: "Broadcasted",
                  value: broadcastResults.broadcasted || 0,
                  color: "#f57d36",
                },
                {
                  label: "Total",
                  value: broadcastResults.total || 0,
                  color: textColor,
                },
                {
                  label: "Errors",
                  value: broadcastResults.errors?.length || 0,
                  color:
                    (broadcastResults.errors?.length || 0) > 0
                      ? "red.500"
                      : "green.500",
                },
              ].map(({ label, value, color }) => (
                <Box key={label}>
                  <Text fontSize="xs" color="gray.500" fontWeight="600">
                    {label}
                  </Text>
                  <Text fontSize="2xl" fontWeight="700" color={color}>
                    {value}
                  </Text>
                </Box>
              ))}
            </HStack>
            {(broadcastResults.errors?.length || 0) > 0 && (
              <Box
                p={4}
                bg="rgba(255,0,0,0.03)"
                borderRadius="8px"
                border="1px solid rgba(255,0,0,0.1)"
              >
                <Text fontSize="sm" fontWeight="600" color="red.500" mb={2}>
                  Errors ({broadcastResults.errors.length}):
                </Text>
                <VStack align="stretch" spacing={1}>
                  {broadcastResults.errors.map((err, idx) => (
                    <Text key={idx} fontSize="xs" color="red.400">
                      {idx + 1}. {err}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}
          </Box>
        </GlassMorphismCard>
      )}

      {/* OCPI Body Modal */}
      <Modal
        isOpen={viewModal.open}
        onClose={closeModal}
        size="4xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(0,0,0,0.4)" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius="8px"
          bg="white"
          boxShadow="0 20px 60px rgba(0,0,0,0.2)"
          maxH="80vh"
        >
          <ModalHeader
            borderBottom="1px solid rgba(245,125,54,0.15)"
            pb={3}
            pr={12}
          >
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={2}>
                <Icon as={MdCode} color="#f57d36" boxSize={5} />
                <Text fontSize="md" fontWeight="700" color="gray.700">
                  OCPI Body — {viewModal.title}
                </Text>
              </Flex>
              {!viewModal.loading && viewModal.body && (
                <Tooltip label="Copy JSON">
                  <IconButton
                    icon={<Icon as={MdContentCopy} />}
                    size="sm"
                    borderRadius="4px"
                    variant="ghost"
                    color="#f57d36"
                    aria-label="Copy JSON"
                    onClick={handleCopyJson}
                  />
                </Tooltip>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="gray.500" top={3} right={3} />
          <ModalBody p={0} overflowY="auto">
            {viewModal.loading ? (
              <Flex justify="center" align="center" py={16}>
                <Spinner color="#f57d36" size="xl" thickness="3px" />
              </Flex>
            ) : viewModal.body ? (
              <Box
                as="pre"
                p={5}
                bg="gray.900"
                color="green.300"
                fontFamily="'Courier New', Courier, monospace"
                fontSize="12px"
                lineHeight="1.7"
                overflowX="auto"
                whiteSpace="pre"
                borderRadius="0 0 8px 8px"
              >
                {JSON.stringify(viewModal.body, null, 2)}
              </Box>
            ) : (
              <Flex justify="center" py={12}>
                <Text color="gray.400" fontSize="sm">
                  No data available.
                </Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Hidden textarea for copy fallback */}
      <textarea
        ref={copyTextareaRef}
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          opacity: 0,
          pointerEvents: "none",
        }}
        readOnly
      />
    </Box>
  );
};

export default RoamingScreen;
